import {
    Box,
    Checkbox,
    CssBaseline,
    FormControl,
    FormControlLabel,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    Tooltip,
    Typography,
    WithStyles,
    withStyles
} from "@material-ui/core";
import { styles } from "./RegisterView.styles";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as UserDetailsAction from "../../stores/user/UserDetailsAction";
import { emailPattern, isEmailValid } from "../../utilities/generalUtils";
import { FormTextField } from "../components/FormComponents/FormTextField";
import { FormNativeSelect } from "../components/FormComponents/FormNativeSelect";
import { BlueButton } from "../components/Buttons/BlueButton";
import { CenteredContainer } from "../components/CenteredContainer/CenteredContainer";
import { useAuthContext } from "../../auth/AuthProvider";
import { useSnackbar } from "notistack";
import * as MessageAction from "../../stores/message/MessageAction";
import ToastStatusEnum from "../../constants/ToastStatusEnum";
import { useHistory, useLocation } from "react-router-dom";
import { PageBanner } from "../components/PageBanner/PageBanner";
import { isUsedOpenAM } from "../../utilities/userUtils";
import { kneipName } from "../../constants/ORDER_DETAILS";
import { selectVisibleCountries } from "../../selectors/countries/CountriesSelector";

export interface ISubscriptionRequestProps extends WithStyles<typeof styles> {
}

const SubscriptionRequestPure: React.FunctionComponent<ISubscriptionRequestProps> = ({classes}) => {
    const {t} = useTranslation();
    const authContext = useAuthContext();
    const userId = authContext?.user?.id;
    const isUser = !!userId;
    const [errors, setErrors] = useState({} as any);
    const dispatch = useDispatch();
    const countries = useSelector(state => selectVisibleCountries(state));
    const userDetails = useSelector(state => state.userDetails?.currentlyLoggedUser);
    const {enqueueSnackbar} = useSnackbar();
    const location = useLocation();
    const history = useHistory();

    const [details, setDetails] = React.useState({
        title: "",
        firstName: "",
        lastName: "",
        company: "",
        country: "",
        email: "",
        subject: location?.state?.subject || "",
        message: "",
        //honeypot
        website: "",
        howDidYouHearAboutDS: "",
        howDidYouHearAboutDSOther: ""
    });

    useEffect(() => {
        if (userId) dispatch(UserDetailsAction.requestCurrentUser(userId));
    }, [dispatch, userId]);

    useEffect(() => {
        if (userDetails)
            setDetails(details => ({
                ...details,
                firstName: userDetails?.firstName,
                lastName: userDetails?.lastName,
                company: userDetails?.company,
                country: userDetails?.billingAddress?.country,
                email: userDetails?.email
            }));
    }, [userDetails]);

    const handleChange = event => {
        const name = event.target.name;
        const value = event.target.value;
        setDetails({
            ...details,
            [name]: value
        });

        let temp: any = {...errors};
        if (event.target.name === "email") {
            if (event.target.value) {
                temp[event.target.name] = isEmailValid(event.target.value) ? "" : "Email is not valid!";
            }
        }
        setErrors({
            ...temp
        });
    };

    const handleCountry = (event) => {
        if (event.target.value !== "") {
            setDetails({
                ...details,
                [event.target.name]: event.target.value
            });
        }
    }

    const itemsForHowDidYouHearAboutUs = [
        {name: "searchEngine", label: t("register.searchEngine")},
        {name: "dbWebsite", label: t("register.dbWebsite")},
        {name: "recommendation", label: t("register.recommendation")}
    ];

    const handleHowDidYouHearAboutUs = (radioValue, textValue) => {
        setDetails({
            ...details,
            howDidYouHearAboutDS: radioValue,
            howDidYouHearAboutDSOther: textValue
        });
    };
    
    const isKneip = location?.state?.product.includes(kneipName);

    const formIsValid = () => { 
        return ((agreeKneip && isKneip) || (!agreeKneip && !isKneip)) // xnor
            && details.email
            && details.subject
            && details.message
            && details.firstName
            && details.lastName
            && Object.values(errors).every(x => x === "");
    };

    const sendContactRequest = event => {
        event.preventDefault();
        const sendDetails = {
            userTitle: details.title,
            firstName: details.firstName,
            lastName: details.lastName,
            company: details.company,
            country: details.country,
            email: details.email,
            subject: details.subject,
            message: details.message,
            envType: isUsedOpenAM() ? "internal" : "external",
            howDidYouHearAboutDS: {
                how: details.howDidYouHearAboutDS,
                other: details.howDidYouHearAboutDSOther
            },
            product: location?.state?.product
        };
        if (details.website === "") {
            dispatch(MessageAction.sendSubscriptionRequest(sendDetails)).then(() => {
                enqueueSnackbar("Message has been sent", {
                    variant: ToastStatusEnum.Info
                });
                isKneip ? history.push("/contact/success-kneip") : history.push("/contact/success");
            });
        }
    };

    const [agreeKneip, setAgreeKneip] = React.useState(false);
    const handleAgreeKneip = () => {
        setAgreeKneip(!agreeKneip);
    };

    return <>
        <PageBanner title={"Subscription Request"} isPublic />
        <CenteredContainer>
            <CssBaseline />
            <Paper className={classes.background}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {isKneip ? 
                        <Typography variant="h3">Your message to Kneip Communication S.A.</Typography>
                        : <Typography variant="h3">{t("contactUs.yourMessage")}</Typography>
                        }
                    </Grid>

                    <Grid item xs={4}>
                        <FormTextField
                            id="title"
                            onChange={handleChange}
                            value={details.title}
                            label={t("contactUs.title")}
                            disabled={isUser}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <FormTextField
                            id="firstName"
                            required
                            onChange={handleChange}
                            value={details.firstName}
                            label={t("general.firstName")}
                            disabled={isUser}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <FormTextField
                            id="lastName"
                            required
                            onChange={handleChange}
                            value={details.lastName}
                            label={t("general.lastName")}
                            disabled={isUser}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <FormTextField
                            id="company"
                            onChange={handleChange}
                            value={details.company}
                            label={t("general.company")}
                            disabled={isUser}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <FormNativeSelect
                            label={t("general.country")}
                            options={countries}
                            onChange={handleCountry}
                            name="country"
                            value={details.country}
                            disabled={isUser}
                            emptyCaption={"Select country"}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <FormTextField
                            id="email"
                            required
                            onChange={handleChange}
                            value={details.email}
                            label={t("general.email")}
                            pattern={emailPattern}
                            disabled={isUser}
                        />
                    </Grid>
                    <Box width="100%" />

                    <Grid item xs={6}>
                        <FormTextField
                            id="subject"
                            required
                            onChange={handleChange}
                            value={details.subject}
                            label={t("contactUs.subject")}
                        />
                    </Grid>
                    <Box width="100%" />

                    <Grid item xs={6}>
                        <FormTextField
                            id="message"
                            required
                            onChange={handleChange}
                            value={details.message}
                            label={t("contactUs.message")}
                            multiline
                            rows={8}
                        />
                    </Grid>
                    <Grid item xs={6} className={classes.website}>
                        <FormTextField id="website" value={details.website} label={"website"}
                                       onChange={handleChange} />
                    </Grid>

                    <Box width="100%" />
                    {!userId && (
                        <div>
                            <Grid item xs={12}>
                                <Typography variant="h3">{t("register.howDidYouHear")}</Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl component="fieldset" fullWidth>
                                    <RadioGroup
                                        value={details.howDidYouHearAboutDS}
                                        onChange={event => handleHowDidYouHearAboutUs(event.target.value, "")}
                                    >
                                        {itemsForHowDidYouHearAboutUs.map(v => (
                                            <FormControlLabel
                                                value={v.name}
                                                control={<Radio />}
                                                label={v.label}
                                                key={v.name}
                                                name={v.name}
                                            />
                                        ))}
                                        <FormControlLabel
                                            value="other"
                                            control={<Radio />}
                                            label={
                                                <FormTextField
                                                    disabled={details.howDidYouHearAboutDS !== "other"}
                                                    label={t("register.other")}
                                                    value={details.howDidYouHearAboutDSOther}
                                                    onChange={event =>
                                                        handleHowDidYouHearAboutUs("other", event.target.value)
                                                    }
                                                />
                                            }
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                        </div>
                    )}
                    <Grid item xs={12}>
                        {isKneip ? <FormControlLabel className={classes.topMargin}
                            control={<Checkbox checked={agreeKneip} onChange={handleAgreeKneip} />}
                            label={<div><Typography variant="body2">I hereby agree that my personal data (such as name, address and e-mail) that I provided above will be transferred by Deutsche Börse AG to Kneip Communication S.A. in order to be able to provide Kneip related Data Services and to fulfill the contract by Kneip Communication S.A.. Kneip is acting as a separate Data Controller in order to provide their own data services and will deliver the respective data separately.
                            Your consent is voluntary. You can revoke your consent at any time without giving reasons with effect for the future. You can send your revocation to <a href="mailto:hdp@deutsche-boerse.com.">hdp@deutsche-boerse.com</a>.
                            Further information on contacts and your rights can be found in <a href="https://a7-dataplatform.deutsche-boerse.com/privacy-notice">our privacy notice</a> and in the <a href="https://www.kneip.com/privacy-statement/">privacy notice of Kneip Communication S.A.</a>.</Typography></div>}
                        /> : null}
                    </Grid>

                    <Grid item xs={12}>
                        <Tooltip title={t("contactUs.validateSend")}>
                            <span>
                                <BlueButton onClick={sendContactRequest} disabled={!formIsValid()}>
                                    {t("contactUs.send")}
                                </BlueButton>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Paper>
        </CenteredContainer>
    </>;
};

export const SubscriptionRequestView = withStyles(styles)(SubscriptionRequestPure);
